import { InputHTMLAttributes, forwardRef, useId } from 'react';
import { text } from '@/config/theme';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      fullWidth = false,
      className = '',
      id,
      disabled,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const errorId = error !== undefined ? `${inputId}-error` : undefined;
    const helperId = helperText !== undefined ? `${inputId}-helper` : undefined;

    const baseClasses =
      'block px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed';
    
    const errorClasses = error !== undefined
      ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
      : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500';

    const widthClass = fullWidth ? 'w-full' : '';

    const inputClasses = `${baseClasses} ${errorClasses} ${widthClass} ${className}`;

    return (
      <div className={fullWidth ? 'w-full' : ''}>
        {label !== undefined && (
          <label
            htmlFor={inputId}
            className={`block text-sm font-medium ${text.secondary} mb-1`}
          >
            {label}
            {props.required === true && (
              <span className="text-red-500 ml-1" aria-label="required">
                *
              </span>
            )}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={inputClasses}
          aria-invalid={error !== undefined}
          aria-describedby={error !== undefined ? errorId : helperId}
          disabled={disabled}
          {...props}
        />
        {error !== undefined && (
          <p id={errorId} className={`mt-1 text-sm ${text.error}`} role="alert">
            {error}
          </p>
        )}
        {helperText !== undefined && error === undefined && (
          <p id={helperId} className={`mt-1 text-sm ${text.tertiary}`}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
